import { Select, ToggleButton } from '@zillow/constellation-10';
import { useMemo } from 'react';
import { useTranslations } from 'next-intl';
import type { CostsAndFees, UnitSelector } from '@/src/types/costAndFees.type';
import { bedPillRow, unitSelect } from './costAndFeesSpoke.styles';

type HdpT = ReturnType<typeof useTranslations<'hdp'>>;

function getBedDisplayName(count: number, t: HdpT): string {
  if (count === 0) return t('studioBedAbbrev');
  return t('bedAbbrev', { count });
}

function getBedroomGroupLabel(count: number, t: HdpT): string {
  if (count === 0) return t('studioBedAbbrev');
  return t('bedroomGroup', { count });
}

type UnitPickerProps = {
  costsData: CostsAndFees;
  selectedBedCount: number | null;
  onBedChange: (bedCount: number | null) => void;
  selectedUnitZpid: string | null;
  onUnitChange: (zpid: string | null) => void;
};

export function UnitPicker({
  costsData,
  selectedBedCount,
  onBedChange,
  selectedUnitZpid,
  onUnitChange,
}: UnitPickerProps) {
  const t = useTranslations('hdp');
  const bedOptions = useMemo(
    () =>
      [...costsData.selectors.beds].filter((bed) => bed.numBeds !== undefined).sort((a, b) => a.numBeds - b.numBeds),
    [costsData.selectors.beds],
  );

  const allUnits = costsData.selectors.units;

  const filteredUnits = useMemo(() => {
    if (selectedBedCount === null) return allUnits;
    return allUnits.filter((unit) => unit.numBeds === selectedBedCount);
  }, [allUnits, selectedBedCount]);

  const hasMultipleBedTypes = bedOptions.length > 1;
  const hasUnits = allUnits.length > 0;
  const showOptgroups = hasMultipleBedTypes && selectedBedCount === null;

  const unitsByBedCount = useMemo(() => {
    if (!showOptgroups) return null;
    const grouped: Record<string, Array<UnitSelector>> = {};
    for (const unit of filteredUnits) {
      const key = String(unit.numBeds);
      if (!grouped[key]) grouped[key] = [];
      grouped[key].push(unit);
    }
    return Object.entries(grouped).sort(([a], [b]) => Number(a) - Number(b));
  }, [filteredUnits, showOptgroups]);

  if (!hasMultipleBedTypes && allUnits.length <= 1) return null;

  const dropdownPlaceholder = (() => {
    const count = filteredUnits.length;
    if (count === 0) return t('noUnitsAvailable');
    return t('unitsAvailableInline', { count });
  })();

  const handleBedClick = (bedCount: number) => {
    if (selectedBedCount === bedCount) {
      onBedChange(null);
    } else {
      onBedChange(bedCount);
    }
    onUnitChange(null);
  };

  const handleUnitSelect = (zpid: string) => {
    if (!zpid) {
      onUnitChange(null);
      onBedChange(null);
      return;
    }
    onUnitChange(zpid);
    const unit = allUnits.find((u) => u.unitZpid === zpid);
    onBedChange(unit?.numBeds ?? null);
  };

  return (
    <div>
      {hasMultipleBedTypes && (
        <div className={bedPillRow} role="group" aria-label={t('filterByBedroomCount')}>
          <ToggleButton
            selected={selectedBedCount === null}
            onClick={() => {
              onBedChange(null);
              onUnitChange(null);
            }}
            size="sm"
          >
            {t('all')}
          </ToggleButton>
          {bedOptions.map((bed) => (
            <ToggleButton
              key={bed.numBeds}
              selected={selectedBedCount === bed.numBeds}
              onClick={() => handleBedClick(bed.numBeds)}
              size="sm"
            >
              {bed.selector?.displayName ?? getBedDisplayName(bed.numBeds, t)}
            </ToggleButton>
          ))}
        </div>
      )}

      {hasUnits && (
        <Select
          className={unitSelect}
          value={selectedUnitZpid ?? ''}
          onChange={(e) => handleUnitSelect(e.target.value)}
          aria-label={t('chooseUnit')}
        >
          <option value="">{dropdownPlaceholder}</option>
          {showOptgroups && unitsByBedCount
            ? unitsByBedCount.map(([bedCount, units]) => (
                <optgroup key={bedCount} label={getBedroomGroupLabel(Number(bedCount), t)}>
                  {units.map((unit) => (
                    <option key={unit.unitZpid} value={unit.unitZpid}>
                      {unit.selector.displayName || getBedDisplayName(unit.numBeds, t)}
                    </option>
                  ))}
                </optgroup>
              ))
            : filteredUnits.map((unit) => (
                <option key={unit.unitZpid} value={unit.unitZpid}>
                  {unit.selector.displayName || getBedDisplayName(unit.numBeds, t)}
                </option>
              ))}
        </Select>
      )}
    </div>
  );
}
